import { Container, Grid, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { localImageSrc } from "app/images";
import classNames from "classnames";
import { defaultFooterSections, FooterLink, FooterSection } from "./footerLinks";
import { NavLink } from "./NavLink";
import { Newsletter } from "components/Newsletter";
import { BRAND_NAME, SHORT_LEGAL_NAME } from "@app/shared/constants";

const getBackgroundStyle = (width?: number) =>
    `url(${localImageSrc("footerBackground1440.png", width)})`;

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        backgroundPositionY: "top",
        backgroundPositionX: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% 60px",
        width: "100%",
        //marginTop: theme.spacing(10),
        paddingTop: "60px",
        paddingBottom: theme.spacing(6),
        zIndex: theme.zIndex.speedDial + 1,
        position: "relative",

        backgroundImage: getBackgroundStyle(400),
        [theme.breakpoints.up(400)]: {
            backgroundImage: getBackgroundStyle(600),
        },
        [theme.breakpoints.up(600)]: {
            backgroundImage: getBackgroundStyle(960),
        },
        [theme.breakpoints.up(960)]: {
            backgroundImage: getBackgroundStyle(1280),
        },
        [theme.breakpoints.up(1280)]: {
            backgroundImage: getBackgroundStyle(),
        },
    },

    link: {
        ...theme.typography.body1,
        color: theme.palette.grey700,
    },

    footerLinks: {
        marginBottom: theme.spacing(0),
    },

    container: {
        padding: theme.spacing(1, 4, 0, 8),
    },

    footerLeft: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        [theme.breakpoints.down("md")]: {
            display: "none",
        },
    },

    title: {
        margin: theme.spacing(1, 0),
    },

    dark: {
        backgroundImage: "none",
        backgroundColor: theme.palette.grey800,
        color: theme.palette.coral100,
        marginTop: 0,

        "& $link": {
            color: theme.palette.coral100,

            "&:hover": {
                color: theme.palette.aqua700,
            },
        },
    },
}));

export const AppFooter = (props: {
    classes?: Partial<ReturnType<typeof useStyles>>;
    dark?: boolean;
    hideNewsletter?: boolean;
    sections?: FooterSection[];
}) => {
    const classes = useStyles(props);

    const sections = props.sections || defaultFooterSections;

    const renderLink = (link: FooterLink) => {
        if (link.external) {
            return (
                <NavLink className={classes.link} to={link.to} target="_blank" underline="none">
                    {link.name}
                </NavLink>
            );
        } else {
            return (
                <NavLink className={classes.link} to={link.to}>
                    {link.name}
                </NavLink>
            );
        }
    };

    return (
        <div
            className={classNames(classes.root, {
                [classes.dark]: props.dark,
            })}
        >
            {!props.hideNewsletter && (
                <div className={classes.dark}>
                    {/* NOTE: currently, the newsletter breaks when not in dark mode, so fix that first if you want to use this on a light background */}
                    <Newsletter dark={true} />
                </div>
            )}

            <Container id="footer" maxWidth="lg" className={classes.container}>
                <Grid container>
                    <Grid item xs={12} md={5} className={classes.footerLeft}>
                        <Typography
                            variant="h3"
                            style={{ fontWeight: 600 }}
                            className={classes.title}
                        >
                            {BRAND_NAME}
                        </Typography>

                        <Typography variant="body2">
                            © {SHORT_LEGAL_NAME}, {new Date().getFullYear()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container>
                            {sections.map((section) => (
                                <Grid item xs={12} sm={4} key={section.title}>
                                    <Typography variant="h4">{section.title}</Typography>
                                    {section.links.map((link) => (
                                        <Typography
                                            variant="body2"
                                            key={link.name}
                                            className={classes.footerLinks}
                                        >
                                            {renderLink(link)}
                                        </Typography>
                                    ))}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
};
